import { Auth } from '@/services/auth-service'
import { Fragment, OnDispose, TNode, Unless } from '@tempots/dom'
import { NavigationService } from '@tempots/ui'

export interface RequireAnonProps {
  /** Optional custom loading message */
  loading?: TNode
  /** Optional redirect path (defaults to '/start') */
  redirectTo?: string
}

/**
 * Renders its content only for anonymous visitors; signed-in users are
 * redirected (default '/start', which forwards to their workspace).
 *
 * `children` MUST be lazy: an eagerly-built subtree runs construction-time
 * side effects — e.g. beatui's PageDropZone attaches document-level drag
 * listeners when constructed — and when the signed-in branch never mounts
 * that subtree, nothing ever cleans those up. A leaked home-page drop
 * listener is how dropping a file on the /customize wizard used to warp
 * users back to the workspace.
 */
export function RequireAnon(
  { loading, redirectTo = '/start' }: RequireAnonProps,
  children: () => TNode
) {
  const unsubscribe = Auth.isSignedIn.on(isSignedIn => {
    if (isSignedIn) {
      NavigationService.navigate(redirectTo, { replace: true })
    }
  })
  return Fragment(
    OnDispose(unsubscribe),
    Unless(Auth.isSignedIn, children, () => loading)
  )
}
